/**
 * Form for an unregistered student on a field event.
 */
import * as React from 'react';
import { Mail } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import { useFormValidation } from '@/hooks/useFormValidation';
import { useDefaultPhoneCountry } from '@/utils/user-contact-utils';
import {
  applyUnregisteredStudentContactChange,
  buildUnregisteredStudentPayload,
  hydrateUnregisteredStudentForm,
} from '@/components/events/event-form-utils';
import { unregisteredStudentValidationSchema } from '@/utils/validation-schemas-utils';
import type { UnregisteredStudentData } from '@/types/event.types';

export type { UnregisteredStudentData };

/** Detected primary-contact kind (`unknown` until the input classifies it). */
type ContactInputType = 'email' | 'phone' | 'unknown';

/** Imperative handle so the parent modal footer can submit. */
export interface UnregisteredStudentFormHandle {
  submit: () => void;
}

/** Props for {@link UnregisteredStudentForm}. */
interface UnregisteredStudentFormProps {
  onAdd: (data: UnregisteredStudentData) => void;
  /** Pre-fill the form with existing data (edit mode). */
  initialData?: UnregisteredStudentData;
}

/** Payload aliases the contact rules expect (`_contactType`, country fields). */
function unregisteredValidationPayload(
  form: ReturnType<typeof hydrateUnregisteredStudentForm>,
): Record<string, unknown> {
  return {
    ...form,
    _contactType: form.primaryContactType,
    contactCountryCode: form.primaryContactCountryCode,
  };
}

/** Form for an unregistered student on a field event. */
export const UnregisteredStudentForm = React.forwardRef<
  UnregisteredStudentFormHandle,
  UnregisteredStudentFormProps
>(({ onAdd, initialData }, ref) => {
  const defaultCountry = useDefaultPhoneCountry();

  const [formData, setFormData] = React.useState(() =>
    hydrateUnregisteredStudentForm(initialData, defaultCountry),
  );

  /** Re-hydrate when `initialData` changes (switching the edited walk-in). */
  React.useEffect(() => {
    setFormData(hydrateUnregisteredStudentForm(initialData, defaultCountry));
  }, [initialData, defaultCountry]);

  const validation = useFormValidation(unregisteredStudentValidationSchema);

  /** Changing email↔phone clears the secondary contact so types stay complementary. */
  const handleContactChange = (value: string, type: ContactInputType, countryCode?: string) => {
    const next = applyUnregisteredStudentContactChange(
      formData,
      value,
      type,
      defaultCountry,
      countryCode,
    );
    setFormData(next);
    validation.validateField('primaryContact', value, unregisteredValidationPayload(next));
    validation.markAsTouched('primaryContact');
    validation.markAsDirty('primaryContact');
  };

  /** Secondary contact is the opposite of the classified primary type. */
  function handleSecondaryContactChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    const next = { ...formData, secondaryContact: value };
    setFormData(next);
    validation.validateField('secondaryContact', value, unregisteredValidationPayload(next));
    validation.markAsTouched('secondaryContact');
    validation.markAsDirty('secondaryContact');
  }

  /** Persist secondary-phone country ISO. */
  function handleSecondaryCountryChange(country: { code: string }): void {
    setFormData((prev) => ({ ...prev, secondaryContactCountryCode: country.code }));
  }

  /** Persist first name and re-validate. */
  function handleFirstNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, firstName: value }));
    validation.validateField('firstName', value, { ...formData, firstName: value });
    validation.markAsDirty('firstName');
  }

  /** Persist last name and re-validate. */
  function handleLastNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, lastName: value }));
    validation.validateField('lastName', value, { ...formData, lastName: value });
    validation.markAsDirty('lastName');
  }

  /** Mark first name touched. */
  function handleFirstNameBlur(): void {
    validation.markAsTouched('firstName');
  }

  /** Mark last name touched. */
  function handleLastNameBlur(): void {
    validation.markAsTouched('lastName');
  }

  /** Mark secondary contact touched. */
  function handleSecondaryBlur(): void {
    validation.markAsTouched('secondaryContact');
  }

  /** Validate, then map local fields to the unregistered-participant payload. */
  const handleSubmit = () => {
    if (!validation.validateForm(unregisteredValidationPayload(formData))) return;
    onAdd(buildUnregisteredStudentPayload(formData));
  };

  React.useImperativeHandle(ref, () => ({ submit: handleSubmit }));

  const primaryError = validation.getFieldError('primaryContact');
  const secondaryError = validation.getFieldError('secondaryContact');

  /** Secondary phone or email, opposite of the primary type. */
  function renderSecondaryContact(): React.ReactNode {
    if (formData.primaryContactType === 'unknown') return null;
    if (formData.primaryContactType === 'email') {
      return (
        <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
          <PhoneInput
            label="Téléphone (contact secondaire, optionnel)"
            value={formData.secondaryContact}
            onChange={handleSecondaryContactChange}
            onBlur={handleSecondaryBlur}
            countryCode={formData.secondaryContactCountryCode}
            onCountryChange={handleSecondaryCountryChange}
            helperText="Numéro secondaire pour joindre ce participant"
            state={secondaryError ? 'error' : 'default'}
            error={secondaryError}
          />
        </div>
      );
    }
    return (
      <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
        <Input
          type="text"
          label="Email (contact secondaire, optionnel)"
          value={formData.secondaryContact}
          onChange={handleSecondaryContactChange}
          onBlur={handleSecondaryBlur}
          icon={Mail}
          placeholder="Ex: contact@example.com"
          helperText="Email secondaire pour joindre ce participant"
          state={secondaryError ? 'error' : 'default'}
          error={secondaryError}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Prénom"
          placeholder="Prénom"
          value={formData.firstName}
          onChange={handleFirstNameChange}
          onBlur={handleFirstNameBlur}
          required
          state={validation.getFieldState('firstName')}
          error={validation.getFieldError('firstName')}
        />
        <Input
          label="Nom"
          placeholder="Nom de famille"
          value={formData.lastName}
          maxLength={2}
          onChange={handleLastNameChange}
          onBlur={handleLastNameBlur}
          required
          state={validation.getFieldState('lastName')}
          error={validation.getFieldError('lastName')}
        />
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Le nom de famille est limité à 2 caractères (anonymisation des participants).
      </p>

      <SmartContactInput
        label="Contact principal"
        value={formData.primaryContact}
        onContactChange={handleContactChange}
        helperText="Saisissez l'email ou le numéro de téléphone du participant (optionnel)"
        state={primaryError ? 'error' : validation.getFieldState('primaryContact')}
        error={primaryError}
      />
      {renderSecondaryContact()}
    </div>
  );
});

UnregisteredStudentForm.displayName = 'UnregisteredStudentForm';
